import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth, homeFor } from "@/auth";
import { useLang } from "@/i18n";
import { fmtErr } from "@/api";

export default function Login({ variant = "investor" }) {
  const { t } = useLang();
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const isDsa = variant === "dsa";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      const allowed = isDsa ? ["dsa", "admin"].includes(user.role) : ["customer", "admin"].includes(user.role);
      if (!allowed) {
        await logout();
        setError(t(isDsa ? "login_wrong_dsa" : "login_wrong_investor"));
        return;
      }
      navigate(homeFor(user.role));
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-6 py-16 ${isDsa ? "bg-[#1A1A1A] noise-overlay" : "bg-[#F3F1EB]"}`} data-testid={isDsa ? "dsa-login-page" : "login-page"}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-10" data-testid="login-home-link">
          <Logo size={44} dark={isDsa} />
        </Link>
        <div className={`bg-white border p-8 sm:p-10 ${isDsa ? "border-[#3A3A3A] shadow-[8px_8px_0_0_#B5952F]" : "border-[#E5E2D9] shadow-[8px_8px_0_0_#E5E2D9]"}`}>
          {isDsa && (
            <div className="inline-block bg-[#1A1A1A] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1.5 mb-4" data-testid="dsa-login-badge">
              {t("login_dsa_badge")}
            </div>
          )}
          <h1 className="font-display text-3xl font-bold text-[#1A1A1A]">{t(isDsa ? "login_dsa_title" : "login_investor_title")}</h1>
          <p className="text-sm text-[#5C5C5C] mt-2 mb-8">{t(isDsa ? "login_dsa_sub" : "login_sub")}</p>
          {error && (
            <div className="mb-6 border border-[#E03131] bg-[#E03131]/5 text-[#E03131] text-sm font-semibold px-4 py-3" data-testid="login-error">
              {error}
            </div>
          )}
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C] mb-2">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-[#E5E2D9] bg-[#FDFCF8] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C]">Password</label>
                <Link to="/forgot-password" className="text-xs font-bold text-[#B5952F] hover:underline" data-testid="forgot-password-link">
                  {t("fp_title")}
                </Link>
              </div>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-[#E5E2D9] bg-[#FDFCF8] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A1A1A] text-[#D4AF37] py-4 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "..." : t("nav_login")}
            </button>
          </form>
          <p className="text-sm text-[#5C5C5C] mt-8 text-center">
            {isDsa ? (
              <>
                {t("login_dsa_new")}{" "}
                <Link to="/register?role=dsa" className="font-bold text-[#B5952F] hover:underline" data-testid="login-register-link">
                  {t("nav_join")}
                </Link>
              </>
            ) : (
              <>
                New to Matrix Works?{" "}
                <Link to="/register" className="font-bold text-[#B5952F] hover:underline" data-testid="login-register-link">
                  {t("nav_join")}
                </Link>
              </>
            )}
          </p>
          <p className="text-xs text-[#5C5C5C] mt-3 text-center">
            <Link to={isDsa ? "/login" : "/dsa-login"} className="font-bold text-[#1A1A1A] hover:text-[#B5952F] transition-colors duration-200" data-testid="login-switch-panel-link">
              {t(isDsa ? "login_investor_link" : "login_dsa_link")} →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
